import { User } from 'modules/roles-manager/domain/interfaces/user';
import roleUsers, { DeleteUserPayload } from 'modules/roles-manager/domain/resources/role-users';
import { useUsersPagesCache } from 'modules/roles-manager/ui/hooks/use-roles-manager-provider';
import { useMutation, useQueryClient } from 'react-query';

export function useDeleteUser(tournamentId?: number) {
  const queryClient = useQueryClient();
  const { resetPagesCache } = useUsersPagesCache();

  return useMutation<boolean, unknown, DeleteUserPayload>(
    (payload) => {
      if (!tournamentId) throw new Error('No tournamentId');
      return roleUsers.deleteUser(tournamentId, payload);
    },
    {
      onSuccess: (_ok, variables) => {
        const deletedId = variables.id;
        const keys = queryClient.getQueryCache().findAll('roleUsers');

        let minOffset: number | null = null;

        keys.forEach((query) => {
          const key = query.queryKey as any[];
          if (!Array.isArray(key)) return;
          if (key[0] !== 'roleUsers') return;
          if (key[1] !== tournamentId) return;

          const pageParams = key[3] as { limit?: number; offset?: number } | undefined;
          const offset = pageParams?.offset ?? null;

          queryClient.setQueryData<User[]>(key, (old) => {
            if (!old) return old as any;

            const next = old.filter((u) => u.id !== deletedId);

            // если длина изменилась — этот запрос действительно содержал удалённого
            if (next.length !== old.length && offset !== null) {
              if (minOffset === null || offset < minOffset) {
                minOffset = offset;
              }
            }

            return next;
          });
        });

        // инвалидируем только "хвост" начиная с найденного offset
        if (minOffset !== null) {
          queryClient.invalidateQueries({
            predicate: (q) => {
              const key = q.queryKey as any[];
              if (!Array.isArray(key)) return false;
              if (key[0] !== 'roleUsers') return false;
              if (key[1] !== tournamentId) return false;

              const pageParams = key[3] as { limit?: number; offset?: number } | undefined;
              const offset = pageParams?.offset ?? null;

              // считаем невалидными все, у кого offset >= minOffset
              return offset !== null && offset >= minOffset!;
            },
          });
        }

        // если были индивидуальные запросы по пользователю
        queryClient.removeQueries(['roleUser', deletedId], { exact: true });
        resetPagesCache();
      },
    },
  );
}

export default useDeleteUser;
